import { appPath, appRouteOf, workAreaRoutePath } from '@featit/shared/addressing/paths';
import { withSelectedItem } from '@/features/views/viewUrlState';

export const WORK_AREAS_HOME_PATH = appPath('/work-areas');
export const WORK_AREAS_ACTIVITY_PATH = appPath('/work-areas/activity');
export const WORK_AREAS_VIEWS_PATH = appPath('/work-areas/views');
export const WORK_AREAS_DOCUMENTS_PATH = appPath('/work-areas/documents');
/** *Decisions for me* across every work area, grouped by work area (I-0560). */
export const WORK_AREAS_DECISIONS_PATH = appPath('/work-areas/decisions');
/** *Tasks for me* across every work area (I-0900), directly below Decisions in the sidebar. */
export const WORK_AREAS_TASKS_PATH = appPath('/work-areas/tasks');
/** *Work items* across every work area (F-0547); an item is selected in its query, as in Views. */
export const WORK_AREAS_ITEMS_PATH = appPath('/work-areas/items');
/** *Schedules* across every work area (U-0065); a schedule opens in its own work area. */
export const WORK_AREAS_SCHEDULES_PATH = appPath('/work-areas/schedules');

/**
 * The namespace scope's own address family (F-0383, §10.9): the same views All work areas offers,
 * filtered to one namespace's whole subtree. `/-/namespaces/:namespaceId[/<sub>]`, mirroring
 * `/-/work-areas[/<sub>]` one level down.
 */
export function namespacePath(namespaceId: string, sub?: string): string {
  return appPath(`/namespaces/${namespaceId}${sub ? `/${sub}` : ''}`);
}

export const NAMESPACE_ACTIVITY_SEGMENT = 'activity';
export const NAMESPACE_DECISIONS_SEGMENT = 'decisions';
export const NAMESPACE_TASKS_SEGMENT = 'tasks';
export const NAMESPACE_VIEWS_SEGMENT = 'views';
export const NAMESPACE_ITEMS_SEGMENT = 'items';

/** The Items list of a scope: one namespace's subtree, or every work area. */
export function scopeItemsPath(namespaceId: string | null | undefined): string {
  return namespaceId ? namespacePath(namespaceId, NAMESPACE_ITEMS_SEGMENT) : WORK_AREAS_ITEMS_PATH;
}

/**
 * Whether the page at `pathname` draws its own panel for `?item=&itemArea=` (F-0547): the Views
 * pages and the scopes' Items lists. The shell's `AllWorkAreasItemPanel` leaves those alone.
 */
export function pageOwnsItemSelection(pathname: string): boolean {
  const route = appRouteOf(pathname);
  if (!route) return false;
  return /^\/(work-areas|namespaces\/[^/]+)\/(views|items)(\/|$)/.test(route);
}

/** Where clicking a namespace lands by default — the same first view All work areas lands on. */
export function namespaceActivityPath(namespaceId: string): string {
  return namespacePath(namespaceId, NAMESPACE_ACTIVITY_SEGMENT);
}

/** Whether `pathname` is inside one namespace's address family. */
export function isNamespaceScopedPath(pathname: string): boolean {
  return appRouteOf(pathname)?.startsWith('/namespaces/') ?? false;
}

/** The namespace id a `/-/namespaces/:namespaceId[/…]` address names, or `null` off that family. */
export function namespaceIdOfPath(pathname: string): string | null {
  const route = appRouteOf(pathname);
  const match = route?.match(/^\/namespaces\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * One decision selected **inside** All work areas (I-0602).
 *
 * Jay, 2026-09-22: *"when selecting a decision in the all work area view remain in that view for
 * working with the details"*. The selection is in the address for the same reason it is on the
 * single-work-area surface (I-0474): a link to a decision is shareable, and the back button does
 * what it looks like it does.
 *
 * ⛔ **The work area is in the path, and it has to be.** A display key is project-local —
 * `work_items` is `@Unique(['projectId', 'key'])` — so `/-/work-areas/decisions/D-0003` names no
 * decision: any number of a reader's work areas may have a `D-0003` waiting for them.
 */
export function workAreasDecisionPath(
  projectSlug: string | null | undefined,
  decisionKey: string | null | undefined,
): string {
  if (!projectSlug || !decisionKey) return WORK_AREAS_DECISIONS_PATH;
  // `/-/work-areas/decisions/acme/tracker/D-0003`: the work area's own path, then the key.
  return `${WORK_AREAS_DECISIONS_PATH}${workAreaRoutePath(projectSlug)}/${encodeURIComponent(decisionKey)}`;
}

/**
 * T-0565, T-0566: one item opened from a scope's Activity feed on a phone, as a pushed screen of its
 * own: `<feed>/acme/tracker/I-0003` under `activityPath` — All work areas' feed
 * (`/-/work-areas/activity`) or an organisation's (`/-/namespaces/:id/activity`). The work area is
 * in the path, because a key is project-local, and the address stays inside the scope, so Back
 * returns to the feed.
 */
export function activityItemPath(
  activityPath: string,
  projectSlug: string | null | undefined,
  itemKey: string | null | undefined,
): string {
  if (!projectSlug || !itemKey) return activityPath;
  return `${activityPath}${workAreaRoutePath(projectSlug)}/${encodeURIComponent(itemKey)}`;
}

/**
 * IU-12b (F-0540): one item opened from a work area's own Activity on a phone, pushed over it as
 * `/acme/tracker/-/activity/I-0003` — the work area is the address's own, so only the key follows.
 * Back returns to that Activity, labelled so (`routeParents`). `commentId` opens it at a comment.
 */
export function workAreaActivityItemPath(
  activityPath: string,
  itemKey: string,
  commentId?: string | null,
): string {
  const query = commentId ? `?${new URLSearchParams({ comment: commentId }).toString()}` : '';
  return `${activityPath}/${encodeURIComponent(itemKey)}${query}`;
}

/**
 * The work area's Activity an item pushed from it (`workAreaActivityItemPath`) belongs to, or
 * `null`. Never one of the scopes' feeds (`/-/work-areas/activity`, an organisation's), which carry
 * the work area in the path (`activityFeedOfItem`).
 */
export function workAreaActivityFeedOfItem(pathname: string): string | null {
  if (pathname.startsWith('/-/')) return null;
  return pathname.match(/^(\/.+\/-\/activity)\/[^/]+$/)?.[1] ?? null;
}

/** The Activity feed `pathname` is — All work areas' or an organisation's — or `null`. */
export function activityFeedOf(pathname: string): string | null {
  if (pathname === WORK_AREAS_ACTIVITY_PATH) return pathname;
  return /^\/-\/namespaces\/[^/]+\/activity$/.test(pathname) ? pathname : null;
}

/** The feed an item pushed from one (`activityItemPath`) belongs to, or `null` for any other path. */
export function activityFeedOfItem(pathname: string): string | null {
  if (pathname.startsWith(`${WORK_AREAS_ACTIVITY_PATH}/`)) return WORK_AREAS_ACTIVITY_PATH;
  return pathname.match(/^(\/-\/namespaces\/[^/]+\/activity)\/.+/)?.[1] ?? null;
}

/**
 * One task selected inside All work areas (I-0954), addressed as a decision is there (I-0602):
 * `/-/work-areas/tasks/acme/tracker/T-0003` — the work area in the path, because a key is
 * project-local.
 */
export function workAreasTaskPath(
  projectSlug: string | null | undefined,
  itemKey: string | null | undefined,
): string {
  if (!projectSlug || !itemKey) return WORK_AREAS_TASKS_PATH;
  return `${WORK_AREAS_TASKS_PATH}${workAreaRoutePath(projectSlug)}/${encodeURIComponent(itemKey)}`;
}

/**
 * One decision selected **inside a namespace's own scope** (F-0383, §10.9 NV-6) — the namespace
 * twin of {@link workAreasDecisionPath}. Without this, `WorkAreasDecisionsPage`'s row still built
 * the All-work-areas address under a namespace, which is `workAreasDecisionPath`'s own former bug
 * (I-0602) re-appearing one scope later: the click answers "open this decision" by also widening
 * the reader's scope, which they never asked for.
 */
export function namespaceDecisionPath(
  namespaceId: string,
  projectSlug: string | null | undefined,
  decisionKey: string | null | undefined,
): string {
  const base = namespacePath(namespaceId, NAMESPACE_DECISIONS_SEGMENT);
  if (!projectSlug || !decisionKey) return base;
  return `${base}${workAreaRoutePath(projectSlug)}/${encodeURIComponent(decisionKey)}`;
}

/** One task selected inside a namespace's own scope (F-0383, §10.9 NV-6), the namespace twin of
 * {@link workAreasTaskPath}. */
export function namespaceTaskPath(
  namespaceId: string,
  projectSlug: string | null | undefined,
  itemKey: string | null | undefined,
): string {
  const base = namespacePath(namespaceId, NAMESPACE_TASKS_SEGMENT);
  if (!projectSlug || !itemKey) return base;
  return `${base}${workAreaRoutePath(projectSlug)}/${encodeURIComponent(itemKey)}`;
}

export function isWorkAreaOverviewPath(pathname: string): boolean {
  const route = appRouteOf(pathname);
  return route === '/work-areas' || Boolean(route?.startsWith('/work-areas/'));
}

/**
 * Whether the scope on screen is **every** work area: the address says so, or the reader chose it.
 *
 * ⭐ One rule, because the sidebar badge, the decisions the store is asked for and the browser tab
 * all have to be about the same scope (I-0611, AC-6). Two readings of "am I under All work areas"
 * is how a badge counting six ends up beside a tab naming one work area — and a tab and a badge
 * that can disagree are worse than a tab with no number on it at all.
 */
export function isAllWorkAreasScope(pathname: string, allWorkAreasSelected: boolean): boolean {
  return isWorkAreaOverviewPath(pathname) || allWorkAreasSelected;
}

/**
 * The sidebar's own Home link, for the scope on screen (F-0383, §10.9 NV-1): All work areas' own
 * Home, a namespace's own Home, or the single selected work area's — in that order, mirroring
 * `Layout.tsx`'s own three-way scope everywhere else. Pulled out as a pure function because
 * `Layout.tsx` has no test file of its own (a pre-existing gap) and this is the one piece of it
 * this item needs proven: a namespace scope used to fall through to `homePath`, the work area left
 * behind, because this branch did not exist at all.
 */
export function sidebarHomePath(scope: {
  overviewMode: boolean;
  namespaceId: string | null;
  homePath: string;
}): string {
  if (scope.overviewMode) return WORK_AREAS_HOME_PATH;
  if (scope.namespaceId) return namespacePath(scope.namespaceId);
  return scope.homePath;
}

/**
 * A work item opened **inside** All work areas (I-0689): the page the reader is on, with the item
 * and its work area added to the query, which the shell's item panel reads.
 *
 * Jay, 2026-09-25: *"when a decision / work item in all workareas is selected remain in all work
 * areas and dont jump to the work area of that item"*. The page underneath is not replaced, so its
 * filters and scroll are still there when the panel closes, and Back closes the panel.
 *
 * ⛔ **The work area is in the address, as an id.** A key is project-local, so the key alone names
 * no item. The parameters are the ones the Views catalog already selects an item with
 * (`item`, `itemArea`), so the product has one way to say "this item, from that work area" in a query.
 */
export function allWorkAreasItemHref(
  here: { pathname: string; search: string },
  workAreaId: string,
  itemKey: string,
): string {
  const search = withSelectedItem(here.search, itemKey, workAreaId);
  return search ? `${here.pathname}?${search}` : here.pathname;
}

/**
 * "Show with list" from an item's full page that holds a scope (F-0547): the scope's Items list with
 * the item selected, so the reader keeps the scope instead of entering the item's work area. `null`
 * when the page does not hold a scope, or holds a single work area (that one has its own list).
 */
export function itemsListWithItemHref(
  scope: { readonly kind: string; readonly namespaceId?: string } | null,
  workAreaId: string,
  itemKey: string,
): string | null {
  if (scope?.kind !== 'all' && scope?.kind !== 'namespace') return null;
  const base = scopeItemsPath(scope.kind === 'namespace' ? (scope.namespaceId ?? null) : null);
  return `${base}?${withSelectedItem('', itemKey, workAreaId)}`;
}
